import React, { useState } from 'react';
import Grid from '@material-ui/core/Grid';
import { BsArrowLeftRight } from 'react-icons/all';
import { useQuery } from 'react-query';
import CircularProgress from '@material-ui/core/CircularProgress';
import WarningIcon from '@material-ui/icons/Warning';
import AppLoader from '../../basic/AppLoader';
import AppTable from '../../basic/AppTable';
import { filterTypes } from '../../basic/AdvancedFilters';
import AppButtonMinimal from '../../basic/AppButtonMinimal';
import { getTheme } from '../../../theme';
import { useAlerts } from '../../../hooks/alerts-hooks';
import { useItems } from '../../../hooks/atoms/items-hook';
import CollectionKeys from '../../../hooks/molecules/collection-keys';
import { useItem } from '../../../hooks/atoms/item-hook';
import { useThematicPackageTrait } from '../../../hooks/molecules/_thematic-packages-traits';
import axiosClient from '../../../http/axios-client';
import { sanitizeInOperator } from '../../../hooks/utils';
import { VersionStatusIcon } from '../association-reports/VersionSelectTable';

const SubTable = (
  {
    thematicPackage,
    cta: {
      title,
      handleBulkAction,
      color,
    },
    traitsFilter,
  },
) => {
  const tableColumns = [
    { id: 1, label: 'ID', field: 'id' },
    { id: 2, label: 'Name', field: 'name' },
    { id: 3,
      label: 'Status',
      field: 'version_status',
      customRenderer({ id: traitId }) {
        const {
          items: versions,
          itemsLoading: versionsLoading,
        } = useItems(CollectionKeys.VERSIONS, {
          filterQuery: {
            'race.id_eq': thematicPackage.race_id.id,
            'device.id_eq': thematicPackage.device_id.id,
            'trait.id_eq': traitId,
            _limit: -1,
          },
        });

        if (versionsLoading) {
          return <CircularProgress />;
        }

        if (!versions || 0 === versions.length) {
          return <WarningIcon style={ { color: 'orange' } } />;
        }

        // TODO: Make it more explicit
        // the last (pending?) version is supposed to be the right one
        const lastVersion = versions[0];

        return <VersionStatusIcon status={ lastVersion.status } />;
      } },
  ];

  const [ filterQuery, setFilterQuery ] = useState({});

  const {
    items: traits,
    itemsLoading: traitsLoading,
    pagination,
  } = useItems(
    CollectionKeys.TRAITS,
    {
      filterQuery: {
        ...traitsFilter,
        ...filterQuery,
      },
    },
  );

  const [ ctaLoading, setCTALoading ] = useState(false);
  const [ selected, setSelected ] = useState([]);

  return (
    <Grid container spacing={ 3 }>
      <Grid item xs={ 12 }>
        <Grid container justify='space-between'>
          <Grid item>
            <AppButtonMinimal
              text='Clear'
              onClick={ () => { return setSelected([]); } }
              disabled={ !selected.length || ctaLoading }
            />
          </Grid>
          <Grid item>
            <AppButtonMinimal
              text={ title }
              color={ color }
              disabled={ ctaLoading || !selected.length }
              onClick={ () => {
                return Promise.resolve()
                  .then(() => {
                    return setCTALoading(true);
                  })
                  .then(() => {
                    return handleBulkAction(selected);
                  })
                  .then(() => {
                    return setSelected([]);
                  })
                  .finally(() => {
                    setCTALoading(false);
                  });
              } }
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={ 12 }>
        <AppTable
          columns={ tableColumns }
          paginationProps={ pagination }
          loading={ traitsLoading || ctaLoading }
          data={ traits?.map((t) => {
            return { ...t, version_status: 1 };
          }) }
          advancedFiltersProps={ {
            properties:
            [
              {
                value: 'name',
                label: 'Name',
                type: filterTypes.FREE_TEXT,
              },
            ],
            initialAndFilterOptions: [],
            onQueryChange: setFilterQuery,
          } }
          selectionProps={ { setSelected, selected } }
        />
      </Grid>
    </Grid>
  );
};

export default function ThematicPackageTraitTransferList(
  {
    id,
  },
) {
  const {
    item: thematicPackage,
    itemIsLoading: thematicPackageLoading,
  } = useItem(CollectionKeys.THEMATIC_PACKAGES, id);

  const {
    data: thematicPackageTraits,
    isLoading: thematicPackageTraitsLoading,
  } = useQuery(
    [ CollectionKeys.THEMATIC_PACKAGES_TRAITS, id, 'traits' ],
    () => {
      return axiosClient.get(`${ CollectionKeys.THEMATIC_PACKAGES_TRAITS }`, {
        params: {
          thematicpackage_id: id,
          _limit: -1,
        },
      }).then((d) => {
        return d.map((dd) => { return dd.trait_id; });
      });
    },
  );

  const {
    addTraitToThematicPackage,
    removeTraitFromThematicPackage,
  } = useThematicPackageTrait();

  const {
    alertSuccess,
  } = useAlerts();

  const handleBulkAddTraits = (traitIds) => {
    const actionPromises = traitIds.map((tId) => {
      return Promise.resolve()
        .then(() => {
          return addTraitToThematicPackage(thematicPackage, { id: tId });
        });
    });

    return Promise.all(actionPromises)
      .then(() => {
        alertSuccess(`Successfully added ${ actionPromises.length } trait(s)`);
      });
  };

  const handleBulkRemoveTraits = (traitIds) => {
    const actionPromises = traitIds.map((tId) => {
      return Promise.resolve()
        .then(() => {
          return removeTraitFromThematicPackage(thematicPackage, { id: tId });
        });
    });

    return Promise.all(actionPromises)
      .then(() => {
        alertSuccess(`Successfully removed ${ actionPromises.length } trait(s)`);
      });
  };

  const associatedTraitIds = thematicPackageTraits?.map((tId) => {
    return tId.id;
  });

  if (thematicPackageLoading) {
    return <AppLoader />;
  }

  if (!thematicPackage.race_id || !thematicPackage.device_id) {
    return <p>The selected thematic package is not associated with a race or a device</p>;
  }

  if (thematicPackageTraitsLoading) {
    return <AppLoader />;
  }

  return (
    <Grid container>
      <Grid item xs={ 12 } md={ 5 } spacing={ 2 } style={ { height: '100%' } }>
        <SubTable
          thematicPackage={ thematicPackage }
          cta={ {
            title: 'Add traits to thematic package',
            handleBulkAction: handleBulkAddTraits,
            color: getTheme().palette.secondary.main,
          } }
          traitsFilter={ {
            id_nin: associatedTraitIds,
          } }
        />
      </Grid>
      <Grid item container xs={ 12 } md={ 1 } alignItems='center' justify='center'>
        <Grid item>
          <BsArrowLeftRight size='48px' color={ getTheme().palette.secondary.main } />
        </Grid>
      </Grid>
      <Grid item xs={ 12 } md={ 6 } spacing={ 2 } style={ { height: '100%' } }>
        <SubTable
          thematicPackage={ thematicPackage }
          cta={ {
            title: 'Remove trait from thematic package',
            handleBulkAction: handleBulkRemoveTraits,
            color: getTheme().palette.error.main,
          } }
          traitsFilter={ {
            id_in: sanitizeInOperator(associatedTraitIds),
          } }
        />
      </Grid>
    </Grid>
  );
}
